import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useAuthState } from "@/modules/auth/presentation/model/useAuthState";
import { ProfileAssignmentHistoryTable } from "@/modules/auth/presentation/ui/ProfileAssignmentHistoryTable";
import { ProfileEmploymentSection } from "@/modules/auth/presentation/ui/ProfileEmploymentSection";
import { ProfileKpiResultsTable } from "@/modules/auth/presentation/ui/ProfileKpiResultsTable";
import type { EmployeeExtendedProfileDTO, EmploymentStatus } from "@/modules/hr/employee-profile/domain/types";
import { computeAge, computeGeneration, daysUntil } from "@/modules/hr/employee-profile/domain/utils";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { config } from "@/shared/lib/config";
import { formatDateLocal } from "@/shared/lib/date-time";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  Banknote,
  Briefcase,
  Building2,
  Calendar,
  CalendarClock,
  ExternalLink,
  FileText,
  Hash,
  Mail,
  MapPin,
  Phone,
  Timer,
  TrendingUp,
  User,
} from "lucide-react";
import { useUserEmployeeProfileQuery } from "../api/queries";

// ── Status styling ──────────────────────────────────────────
const STATUS_META: Record<
  EmploymentStatus,
  { label: string; color: string; bg: string; border: string }
> = {
  active: {
    label: "ຍັງເຮັດວຽກ",
    color: "text-emerald-700 dark:text-emerald-400",
    bg: "bg-emerald-50 dark:bg-emerald-950/40",
    border: "border-emerald-200 dark:border-emerald-800",
  },
  probation: {
    label: "ທົດລອງງານ",
    color: "text-amber-700 dark:text-amber-400",
    bg: "bg-amber-50 dark:bg-amber-950/40",
    border: "border-amber-200 dark:border-amber-800",
  },
  probation_failed: {
    label: "ບໍ່ຜ່ານທົດລອງ",
    color: "text-orange-700 dark:text-orange-400",
    bg: "bg-orange-50 dark:bg-orange-950/40",
    border: "border-orange-200 dark:border-orange-800",
  },
  resigned: {
    label: "ລາອອກ",
    color: "text-slate-600 dark:text-slate-400",
    bg: "bg-slate-50 dark:bg-slate-900/40",
    border: "border-slate-200 dark:border-slate-700",
  },
  terminated: {
    label: "ຖືກເລີກຈ້າງ",
    color: "text-red-700 dark:text-red-400",
    bg: "bg-red-50 dark:bg-red-950/40",
    border: "border-red-200 dark:border-red-800",
  },
};

function computeTenure(startDate: string): string {
  const start = new Date(startDate);
  const now = new Date();
  let years = now.getFullYear() - start.getFullYear();
  let months = now.getMonth() - start.getMonth();
  if (months < 0) { years--; months += 12; }
  if (years > 0 && months > 0) return `${years} ປີ ${months} ເດືອນ`;
  if (years > 0) return `${years} ປີ`;
  if (months > 0) return `${months} ເດືອນ`;
  const days = Math.floor((now.getTime() - start.getTime()) / 86_400_000);
  return `${days} ວັນ`;
}

// ── Shared small row component ───────────────────────────────
function InfoItem({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div className="flex items-start gap-2">
      {icon && (
        <span className="mt-0.5 shrink-0 text-muted-foreground">{icon}</span>
      )}
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p className="text-sm font-medium">{value ?? "—"}</p>
      </div>
    </div>
  );
}

// ── Personal Info card (from hr_employee_profile) ────────────
function PersonalInfoCard({ ext }: { ext: EmployeeExtendedProfileDTO }) {
  const age = ext.dateOfBirth ? computeAge(ext.dateOfBirth) : null;
  const gen = ext.dateOfBirth ? computeGeneration(ext.dateOfBirth) : null;
  const currentAddr = [ext.currentVillage, ext.currentDistrict, ext.currentProvince]
    .filter(Boolean)
    .join(", ") || null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <User className="size-4" />
          ຂໍ້ມູນສ່ວນຕົວ
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-x-6 gap-y-4">
        <InfoItem
          icon={<Calendar className="size-3.5" />}
          label="ວັນເກີດ"
          value={ext.dateOfBirth ? formatDateLocal(ext.dateOfBirth) : null}
        />
        <InfoItem
          label="ອາຍຸ / Gen"
          value={age !== null ? `${age} ປີ · ${gen}` : null}
        />
        <InfoItem label="ເພດ" value={
          ext.gender === "male" ? "ຊາຍ" : ext.gender === "female" ? "ຍິງ" : ext.gender === "other" ? "ອື່ນໆ" : null
        } />
        <InfoItem label="ສະຖານະສົມລົດ" value={
          ext.maritalStatus === "single" ? "ໂສດ"
          : ext.maritalStatus === "married" ? "ແຕ່ງງານ"
          : ext.maritalStatus === "divorced" ? "ຢ່າຮ້າງ"
          : ext.maritalStatus === "widowed" ? "ໝ້າຍ"
          : null
        } />
        <InfoItem label="ສັນຊາດ" value={ext.nationality} />
        <InfoItem label="ຊົນເຜົ່າ" value={ext.ethnicity} />
        <InfoItem label="ສາສະໜາ" value={ext.religion} />
        <InfoItem label="ກຸ່ມເລືອດ" value={ext.bloodType} />
        {currentAddr && (
          <div className="col-span-2 flex items-start gap-2">
            <MapPin className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                ທີ່ຢູ່ປັດຈຸບັນ
              </p>
              <p className="text-sm font-medium">{currentAddr}</p>
            </div>
          </div>
        )}
        {ext.documentFileKey && (
          <div className="col-span-2">
            <a
              href={`${config.apiUrl}/files/${ext.documentFileKey}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
            >
              <FileText className="size-4" />
              ເປີດໄຟລ໌ເອກກະສານ
              <ExternalLink className="size-3" />
            </a>
          </div>
        )}
        {!ext.documentFileKey && ext.documentFilesUrl && (
          <div className="col-span-2">
            <a
              href={ext.documentFilesUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
            >
              <ExternalLink className="size-4" />
              ໄຟລ໌ເອກກະສານ
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Page ────────────────────────────────────────────────
export function UserProfilePage() {
  const { userId } = useParams({ from: "/app/users/$userId/profile" });
  const nav = useNavigate();
  const { hasPermission, hasAnyPermission, isLoading: authLoading } = useAuthState();

  const { data: profile, isPending: profileLoading, isError: profileError } =
    useUserEmployeeProfileQuery(userId, { enabled: !!userId });

  const canViewJd = hasPermission(Permissions.jd.read);
  const canViewKpiDashboard = hasAnyPermission([
    Permissions.kpi.read,
    Permissions.mgmt_okr_kpi.read,
  ]);
  const canViewPromotionRecords = hasPermission(Permissions.hr_promotion.record_read);
  const canEditEmployeeProfile = hasPermission(Permissions.hr_employee_profile.upsert);
  const canReadSensitive = hasPermission(Permissions.hr_employee_profile.read_sensitive);

  if (authLoading || profileLoading || !userId) {
    return (
      <>
        <Header />
        <Main>
          <div className="space-y-4">
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </Main>
      </>
    );
  }

  if (profileError || !profile) {
    return (
      <>
        <Header />
        <Main>
          <p className="text-destructive text-sm">
            ບໍ່ສາມາດໂຫຼດຂໍ້ມູນໂປຣໄຟລ໌ — ລອງຣີເຟຣຊ ຫຼື ກວດສິດທິ
          </p>
        </Main>
      </>
    );
  }

  const ext = profile.employeeExtended;
  const status: EmploymentStatus = ext?.status ?? "active";
  const meta = STATUS_META[status];

  const displayName = ext?.nameEn ?? ext?.nameLao ?? profile.user.name ?? "—";
  const altName = ext?.nameLao && ext?.nameEn ? ext.nameLao : null;

  const initials = ((profile.user.name ?? profile.user.email) || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const startDate = profile.employment?.startDate ?? null;
  const tenure = startDate ? computeTenure(startDate) : null;
  const probationDays = ext?.probationEndDate ? daysUntil(ext.probationEndDate) : null;

  return (
    <>
      <Header />
      <Main>
        {/* Back button */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mb-4 -ml-2 gap-1"
          onClick={() => nav({ to: "/app/users" })}
        >
          <ArrowLeft className="size-4" />
          ກັບໄປລາຍການຜູ້ໃຊ້
        </Button>

        {/* ── Hero Card ── */}
        <Card className="mb-5">
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-start gap-5">
              {/* Avatar */}
              <Avatar className="size-20 shrink-0 ring-2 ring-border">
                <AvatarImage src={profile.user.image ?? undefined} />
                <AvatarFallback className="text-xl font-bold">{initials}</AvatarFallback>
              </Avatar>

              {/* Main info */}
              <div className="min-w-0 flex-1 space-y-1.5">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-bold text-2xl leading-tight">{displayName}</h1>
                  {altName && (
                    <span className="text-muted-foreground">({altName})</span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  {ext?.employeeCode && (
                    <span className="flex items-center gap-1">
                      <Hash className="size-3.5" />
                      <span className="font-mono font-semibold text-foreground">
                        {ext.employeeCode}
                      </span>
                    </span>
                  )}
                  {ext?.branch && (
                    <Badge variant="outline" className="font-normal">
                      {ext.branch}
                    </Badge>
                  )}
                  {profile.employment?.positionTitle && (
                    <span className="flex items-center gap-1">
                      <Briefcase className="size-3.5" />
                      {profile.employment.positionTitle}
                    </span>
                  )}
                  {profile.employment?.departmentName && (
                    <span className="flex items-center gap-1">
                      <Building2 className="size-3.5" />
                      {profile.employment.departmentName}
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                  <a
                    href={`mailto:${profile.user.email}`}
                    className="flex items-center gap-1 hover:text-foreground"
                  >
                    <Mail className="size-3.5" />
                    {profile.user.email}
                  </a>
                  {profile.user.phoneNumber && (
                    <span className="flex items-center gap-1">
                      <Phone className="size-3.5" />
                      {profile.user.phoneNumber}
                    </span>
                  )}
                  {startDate && (
                    <span className="flex items-center gap-1">
                      <Calendar className="size-3.5" />
                      ເລີ່ມ {formatDateLocal(startDate)}
                      {tenure && ` · ${tenure}`}
                    </span>
                  )}
                </div>
              </div>

              {/* Status + actions */}
              <div className="flex shrink-0 flex-col items-end gap-2">
                {ext && (
                  <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold ${meta.bg} ${meta.border} ${meta.color}`}>
                    <span className="size-2 rounded-full bg-current opacity-70" />
                    {meta.label}
                  </span>
                )}
                {status === "probation" && probationDays !== null && (
                  <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                    <Timer className="size-3.5" />
                    {probationDays > 0
                      ? `ທົດລອງ ເຫຼືອ ${probationDays} ວັນ`
                      : probationDays === 0
                      ? "ທົດລອງ ຄົບວັນນີ້"
                      : `ທົດລອງ ເກີນ ${Math.abs(probationDays)} ວັນ`}
                  </span>
                )}
                {canEditEmployeeProfile && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    onClick={() =>
                      nav({ to: "/app/hr/employees/$userId", params: { userId } })
                    }
                  >
                    <User className="size-4" />
                    ແກ້ໄຂໂປຣໄຟລ໌
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Employment + Personal Info (side by side) ── */}
        <div className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <ProfileEmploymentSection
            reportingLine={profile.reportingLine}
            employment={profile.employment}
            jd={profile.jd}
            currentSalary={profile.currentSalary}
            kpiSummary={profile.kpiSummary}
            canViewJd={canViewJd}
            canViewKpiDashboard={canViewKpiDashboard}
          />
          {ext ? (
            <PersonalInfoCard ext={ext} />
          ) : (
            <Card className="flex items-center justify-center">
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                <User className="mx-auto mb-2 size-8 opacity-30" />
                ຍັງບໍ່ມີຂໍ້ມູນສ່ວນຕົວ
                <br />
                {canEditEmployeeProfile && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="mt-1"
                    onClick={() =>
                      nav({ to: "/app/hr/employees/$userId", params: { userId } })
                    }
                  >
                    ເພີ່ມຂໍ້ມູນ
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* ── Assignment History ── */}
        <Card className="mb-5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              <CalendarClock className="size-4" />
              ປະຫວັດຕຳແໜ່ງ
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ProfileAssignmentHistoryTable rows={profile.assignmentHistory} />
          </CardContent>
        </Card>

        {/* ── KPI Detail ── */}
        <Card className="mb-5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              <TrendingUp className="size-4" />
              KPI (ລາຍລະອຽດ)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ProfileKpiResultsTable rows={profile.kpiResults} />
          </CardContent>
        </Card>

        {/* ── Career Path ── */}
        {canViewPromotionRecords && (
          <Card className="mb-5">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                <TrendingUp className="size-4" />
                ເສັ້ນທາງການເຕີບໂຕ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  nav({
                    to: "/app/hr/promotion/records",
                    search: {
                      offset: 0,
                      limit: 20,
                      sort: undefined,
                      employeeId: userId,
                      periodYear: undefined,
                      periodMonth: undefined,
                      positionId: undefined,
                      search: undefined,
                    },
                  })
                }
              >
                ເບິ່ງບັນທຶກການເລື່ອນຕຳແໜ່ງ
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── Account & Contact ── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              <Mail className="size-4" />
              ບັນຊີ ແລະ ການຕິດຕໍ່
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <InfoItem icon={<Mail className="size-3.5" />} label="ອີເມວ" value={profile.user.email} />
            <InfoItem
              icon={<Phone className="size-3.5" />}
              label="ເບີໂທ"
              value={profile.user.phoneNumber?.trim() || null}
            />
            {canReadSensitive && (ext?.bankAccountName || ext?.bankAccountNumber) && (
              <>
                <InfoItem
                  icon={<Banknote className="size-3.5" />}
                  label="ຊື່ບັນຊີທະນາຄານ"
                  value={ext.bankAccountName}
                />
                <InfoItem
                  icon={<Banknote className="size-3.5" />}
                  label="ເລກບັນຊີທະນາຄານ"
                  value={ext.bankAccountNumber}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
